import { useEffect, useState } from 'react'

/**
 * Compact NAV-deltas strip — Today / 7d / 30d. Pulls from
 * /api/pf-health which stitches live-pnl + daily_snapshots.
 *
 * Renders nothing while loading, and gracefully omits any pill
 * whose data isn't available yet (eg. WoW before snapshots reach
 * back 7 days).
 */

interface HealthSummary {
  today_change_gbp?: number | null
  today_change_pct?: number | null
  wow_change_gbp?: number | null
  mom_change_gbp?: number | null
}

function fmtGBP(n: number): string {
  const sign = n >= 0 ? '+' : ''
  if (Math.abs(n) >= 1_000_000) return `${sign}£${(n / 1_000_000).toFixed(2)}M`
  if (Math.abs(n) >= 1_000)     return `${sign}£${(n / 1_000).toFixed(0)}k`
  return `${sign}£${n.toFixed(0)}`
}

function Pill({ label, gbp, pct }: {
  label: string; gbp: number | null | undefined; pct?: number | null
}) {
  if (gbp == null) return null
  const tone = gbp >= 0
    ? 'bg-emerald/10 text-emerald-light border-emerald/20'
    : 'bg-rose/10 text-rose-light border-rose/20'
  const arrow = gbp >= 0 ? '▲' : '▼'
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-mono ${tone}`}>
      <span className="text-text-muted text-[10px] uppercase tracking-wider">{label}</span>
      <span className="font-semibold">{arrow} {fmtGBP(gbp)}</span>
      {pct != null && (
        <span className="text-[10px] opacity-80">
          ({gbp >= 0 ? '+' : ''}{pct.toFixed(2)}%)
        </span>
      )}
    </div>
  )
}

export function PerformanceStrip() {
  const [data, setData] = useState<HealthSummary | null>(null)

  useEffect(() => {
    let alive = true
    const fetchOnce = async () => {
      try {
        const res = await fetch('/api/pf-health', {
          headers: { Authorization: `Bearer ${localStorage.getItem('av-access-token') || ''}` },
        })
        if (!res.ok || !alive) return
        setData(await res.json())
      } catch { /* silent */ }
    }
    fetchOnce()
    const t = setInterval(fetchOnce, 60_000)
    return () => { alive = false; clearInterval(t) }
  }, [])

  if (!data) return null
  const today = data.today_change_gbp
  const wow = data.wow_change_gbp
  const mom = data.mom_change_gbp
  if (today == null && wow == null && mom == null) return null

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Pill label="TODAY" gbp={today} pct={data.today_change_pct} />
      <Pill label="7D" gbp={wow} />
      <Pill label="30D" gbp={mom} />
    </div>
  )
}
